import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play, ArrowDown, ArrowUpRight, Layers } from 'lucide-react';
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap-config';
import './layout-cinema.css';

// All three perspectives retain the same four content blocks.
const layouts = [
  [[3, 5, 57, 51], [62, 5, 35, 51], [3, 59, 35, 36], [40, 59, 57, 36]],
  [[3, 19, 22, 66], [27, 19, 22, 66], [51, 19, 22, 66], [75, 19, 22, 66]],
  [[3, 5, 94, 21], [3, 28, 94, 21], [3, 51, 94, 21], [3, 74, 94, 21]],
];
const glyphs = [
  'M2 2H13V13H2Z M16 2H24V13H16Z M2 16H10V24H2Z M13 16H24V24H13Z',
  'M2 2H6V24H2Z M8 2H12V24H8Z M14 2H18V24H14Z M20 2H24V24H20Z',
  'M2 2H24V6H2Z M2 8H24V12H2Z M2 14H24V18H2Z M2 20H24V24H2Z',
];

/** Small, locally rendered image sequence; no network assets or per-frame React updates. */
function makeFrames() {
  return Array.from({ length: 36 }, (_, frame) => {
    const image = document.createElement('canvas');
    image.width = 640;
    image.height = 360;
    const ctx = image.getContext('2d');
    if (!ctx) return image;
    const progress = frame / 35;
    const glow = ctx.createRadialGradient(320 + progress * 100, 170, 5, 320, 180, 340);
    glow.addColorStop(0, '#244c773b');
    glow.addColorStop(1, '#080e1800');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, 640, 360);
    ctx.strokeStyle = '#71a9e52a';
    ctx.lineWidth = 0.6;
    for (let line = 0; line < 17; line++) {
      const x = (line - 8) * 75;
      ctx.beginPath();
      ctx.moveTo(320 + x * 0.2, 120 - progress * 25);
      ctx.lineTo(320 + x * (1.6 + progress * 0.4), 380);
      ctx.stroke();
    }
    for (let line = 0; line < 12; line++) {
      const depth = (line + progress) / 12;
      const y = 125 + depth * depth * 260;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(640, y); ctx.stroke();
    }
    return image;
  });
}

export function LayoutCinema() {
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const pausedRef = useRef(false);
  const visibleRef = useRef(false);
  const [paused, setPaused] = useState(false);

  useGSAP(() => {
    const element = root.current;
    if (!element) return;
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const cards = gsap.utils.toArray<HTMLElement>('.lc-card', element);
      const phases = gsap.utils.toArray<HTMLElement>('.lc-phase', element);
      const glyph = element.querySelector('.lc-glyph');
      const route = element.querySelector<SVGPathElement>('.lc-route');
      const frames = makeFrames();
      const playhead = { frame: 0 };
      const context = canvas.current?.getContext('2d');
      const paint = () => {
        if (!context || !canvas.current) return;
        context.clearRect(0, 0, 640, 360);
        context.drawImage(frames[Math.round(playhead.frame)], 0, 0);
      };
      paint();
      const sequence = gsap.to(playhead, {
        frame: frames.length - 1, ease: 'none', onUpdate: paint,
        scrollTrigger: { trigger: element, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
      });
      const tl = gsap.timeline({ paused: true, repeat: -1, defaults: { ease: 'power3.inOut', duration: 1.5 } });
      gsap.set('.lc-progress', { scaleX: 0, transformOrigin: 'left' });
      const activate = (index: number) => {
        element.dataset.layout = String(index);
        phases.forEach((phase, i) => {
          phase.dataset.active = String(i === index);
          if (i === index) phase.setAttribute('aria-current', 'step');
          else phase.removeAttribute('aria-current');
        });
      };
      [0, 1, 2, 0].forEach((index, step) => {
        const at = step * 5;
        tl.call(() => activate(index), [], at);
        cards.forEach((card, i) => {
          const [left, top, width, height] = layouts[index][i];
          tl.to(card, { left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, z: index === 1 ? i * 12 : 0 }, at);
        });
        tl.to(glyph, { morphSVG: glyphs[index] }, at);
        tl.to('.lc-camera', { rotationX: index === 1 ? 9 : index === 2 ? 0 : 5, rotationY: index === 1 ? -13 : index === 2 ? 0 : -5, rotationZ: index === 1 ? -2 : 0, scale: index === 1 ? 0.94 : 1 }, at);
        tl.fromTo('.lc-reveal', { clipPath: 'circle(0% at 20% 80%)', opacity: 0.6 }, { clipPath: 'circle(140% at 20% 80%)', opacity: 0, duration: 1.8 }, at);
        tl.fromTo(route, { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 2.4 }, at);
        tl.fromTo('.lc-probe', { opacity: 1 }, { motionPath: { path: route!, align: route!, alignOrigin: [0.5, 0.5] }, duration: 2.4, ease: 'power1.inOut' }, at);
        tl.to('.lc-probe', { opacity: 0, duration: 0.4 }, at + 2.4);
      });
      tl.fromTo('.lc-progress', { scaleX: 0 }, { scaleX: 1, duration: 16.5, ease: 'none' }, 0);
      const syncPlayback = () => {
        const running = visibleRef.current && !pausedRef.current && !document.hidden;
        tl.paused(!running);
        if (sequence.scrollTrigger) {
          if (pausedRef.current || document.hidden) sequence.scrollTrigger.disable(false);
          else sequence.scrollTrigger.enable(false);
        }
      };
      const trigger = ScrollTrigger.create({
        trigger: element, start: 'top 85%', end: 'bottom 15%',
        onToggle: (self) => { visibleRef.current = self.isActive; syncPlayback(); },
      });
      visibleRef.current = trigger.isActive;
      syncPlayback();
      document.addEventListener('visibilitychange', syncPlayback);
      element.addEventListener('cinema-playback', syncPlayback);
      return () => {
        document.removeEventListener('visibilitychange', syncPlayback);
        element.removeEventListener('cinema-playback', syncPlayback);
        delete element.dataset.layout;
        phases.forEach((phase, i) => {
          phase.dataset.active = String(i === 0);
          if (i === 0) phase.setAttribute('aria-current', 'step');
          else phase.removeAttribute('aria-current');
        });
        frames.forEach((frame) => { frame.width = 0; frame.height = 0; });
        context?.clearRect(0, 0, 640, 360);
      };
    });
    return () => media.revert();
  }, { scope: root, dependencies: [i18n.resolvedLanguage], revertOnUpdate: true });

  const togglePlayback = () => {
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
    root.current?.dispatchEvent(new Event('cinema-playback'));
  };

  return (
    <div className="layout-cinema" ref={root}>
      <div className="lc-heading">
        <p className="lc-eyebrow">[{t('morph.act')}]</p>
        <div className="lc-heading-row">
          <h2>{t('morph.title')} <span>{t('morph.titleAccent')}</span></h2>
          <p className="lc-intro">{t('morph.cinemaIntro')}</p>
        </div>
      </div>
      <div className="lc-theater">
        <div className="lc-toolbar">
          <span className="lc-toolbar-label"><Layers size={14} aria-hidden="true" /> LAMSLIDE <span>/</span> {t('morph.studio')}</span>
          <button className="lc-playback" onClick={togglePlayback} aria-label={t(paused ? 'morph.resume' : 'morph.pause')}>
            {paused ? <Play size={13} aria-hidden="true" /> : <Pause size={13} aria-hidden="true" />}
            {t(paused ? 'morph.resume' : 'morph.autoPlay')}
          </button>
        </div>
        <div className="lc-viewport">
          <canvas ref={canvas} width={640} height={360} className="lc-canvas" aria-hidden="true" />
          <div className="lc-orbit lc-orbit-one" aria-hidden="true" />
          <div className="lc-orbit lc-orbit-two" aria-hidden="true" />
          <div className="lc-side-note" aria-hidden="true">{t('morph.oneStory')}<span>16:9 / 04</span></div>
          <div className="lc-camera">
            <div className="lc-backplate" aria-hidden="true" />
            <div className="lc-slide">
              <div className="lc-slide-heading"><span>{t('morph.deckLabel')}</span><span>2026 — Q4 <ArrowUpRight size={12} aria-hidden="true" /></span></div>
              <div className="lc-composition">
                {[0, 1, 2, 3].map((index) => {
                  const [left, top, width, height] = layouts[0][index];
                  return (
                    <article className={`lc-card lc-card-${index}`} key={index} style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}>
                      <div className="lc-card-copy"><span className="lc-card-number">0{index + 1} / {t(`morph.slides.${index}.label`)}</span><h3>{t(`morph.slides.${index}.title`)}</h3></div>
                      {index === 0 && <div className="lc-card-art lc-stat"><strong>24<span>%</span></strong><span>{t('morph.growth')}</span><svg viewBox="0 0 160 48" aria-hidden="true"><path d="M0 44L22 37L45 39L69 20L94 26L124 9L160 2" fill="none" stroke="currentColor" strokeWidth="3" /></svg></div>}
                      {index === 1 && <div className="lc-card-art lc-bars" aria-hidden="true">{[32, 48, 40, 65, 80, 98].map((height, i) => <i key={i} style={{ height: `${height}%` }} />)}</div>}
                      {index === 2 && <div className="lc-card-art lc-rings" aria-hidden="true"><i /><i /><i /></div>}
                      {index === 3 && <div className="lc-card-art lc-roadmap" aria-hidden="true"><span>01</span><i /><span>02</span><i /><span>03</span></div>}
                    </article>
                  );
                })}
                <div className="lc-reveal" aria-hidden="true" />
              </div>
              <div className="lc-slide-footer"><span>LAMSLIDE®</span><span>{t('morph.oneStory')}</span><span>01 — 04</span></div>
            </div>
          </div>
          <svg className="lc-guide" viewBox="0 0 1000 580" fill="none" aria-hidden="true"><path className="lc-route" d="M90 430C10 250 220 50 500 60S1030 270 900 430C790 560 320 540 200 470" stroke="#7db8e9" strokeWidth="1" /><circle className="lc-probe" r="4" fill="#c4edff" /></svg>
          <div className="lc-caption"><svg viewBox="0 0 26 26" aria-hidden="true"><path className="lc-glyph" d={glyphs[0]} fill="currentColor" /></svg><span>{t('morph.sameContent')}</span></div>
        </div>
        <div className="lc-phases" aria-label={t('morph.perspectives')}>
          {['Bento', 'Storyline', 'Table'].map((mode, index) => <div className="lc-phase" data-active={index === 0 ? 'true' : 'false'} aria-current={index === 0 ? 'step' : undefined} key={mode}><span className="lc-phase-number">0{index + 1}</span><div><h3>{t(`morph.mode${mode}`)}</h3><p>{t(`morph.phase${index}`)}</p></div><ArrowUpRight size={16} aria-hidden="true" /></div>)}
        </div>
        <div className="lc-progress" aria-hidden="true" />
      </div>
      <p className="lc-scroll-hint"><ArrowDown size={12} aria-hidden="true" />{t('morph.scrollHint')}</p>
    </div>
  );
}
